import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { AlertCircle, Coins, TrendingUp } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { proventosService, type ProventosData } from '@/services';
import { formatCurrency } from '@/utils/format-currency';
import './proventos.css';

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
};

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  return `${MONTH_LABELS[m] ?? m}/${year.slice(2)}`;
}

function formatDateBr(iso: string | null): string {
  if (!iso) return '--';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

function AssetTypeBadge({ type }: { type: string }) {
  const cls = type === 'FII'
    ? 'proventos__badge proventos__badge--fii'
    : 'proventos__badge proventos__badge--acao';
  return <span className={cls}>{type}</span>;
}

export function Proventos() {
  const {
    data,
    isLoading,
    isError,
  } = useQuery<ProventosData>({
    queryKey: ['proventos'],
    queryFn: proventosService.getProventos,
    staleTime: 1000 * 60 * 30,
  });

  const projection = data?.nextMonthProjection ?? { fiiTotal: 0, otherTotal: 0 };
  const assets = data?.assets ?? [];
  const totalProjection = projection.fiiTotal + projection.otherTotal;

  const chartData = useMemo(
    () => (data?.monthlyHistory ?? []).map((m) => ({
      name: formatMonth(m.month),
      FIIs: Number(m.fiiTotal.toFixed(2)),
      'Ações/BDRs': Number(m.otherTotal.toFixed(2)),
    })),
    [data?.monthlyHistory]
  );

  const assetsWithDividends = assets.filter((a) => a.lastDividendValue > 0);
  const isEmpty = !isLoading && assetsWithDividends.length === 0;

  return (
    <Layout>
      <div className="proventos app-page">
        {/* Disclaimer */}
        <div className="proventos__disclaimer" role="note">
          <AlertCircle size={14} aria-hidden="true" />
          <span>
            Valores estimados com base no último provento declarado. FIIs pagam mensalmente;
            ações têm frequência variável. Rendimentos passados não garantem rendimentos futuros.
          </span>
        </div>

        {isError && (
          <div className="proventos__error" role="alert">
            Erro ao carregar proventos. Tente novamente.
          </div>
        )}

        {/* Summary cards */}
        <section className="proventos__cards">
          <div className="proventos__card proventos__card--highlight">
            <div className="proventos__card-header">
              <span className="proventos__card-icon proventos__card-icon--fii">
                <Coins size={16} />
              </span>
              <span className="proventos__card-label">Estimativa FIIs — próximo mês</span>
            </div>
            {isLoading ? (
              <div className="proventos__skeleton proventos__skeleton--lg" />
            ) : (
              <strong className="proventos__card-value">{formatCurrency(projection.fiiTotal)}</strong>
            )}
            <span className="proventos__card-hint">Baseado no último rendimento de cada FII</span>
          </div>

          <div className="proventos__card">
            <div className="proventos__card-header">
              <span className="proventos__card-icon proventos__card-icon--acao">
                <TrendingUp size={16} />
              </span>
              <span className="proventos__card-label">Último dividendo — Ações/BDRs</span>
            </div>
            {isLoading ? (
              <div className="proventos__skeleton proventos__skeleton--lg" />
            ) : (
              <strong className="proventos__card-value">{formatCurrency(projection.otherTotal)}</strong>
            )}
            <span className="proventos__card-hint">Frequência variável (trimestral, semestral...)</span>
          </div>

          <div className="proventos__card proventos__card--total">
            <div className="proventos__card-header">
              <span className="proventos__card-label">Total estimado</span>
            </div>
            {isLoading ? (
              <div className="proventos__skeleton proventos__skeleton--lg" />
            ) : (
              <strong className="proventos__card-value proventos__card-value--total">
                {formatCurrency(totalProjection)}
              </strong>
            )}
          </div>
        </section>

        {/* Monthly history chart */}
        <section className="proventos__chart-section">
          <h2 className="proventos__section-title">Evolução histórica de proventos</h2>
          {isLoading ? (
            <div className="proventos__chart-skeleton" />
          ) : chartData.length === 0 ? (
            <p className="proventos__empty-text">Nenhum histórico disponível ainda.</p>
          ) : (
            <div className="proventos__chart">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} barSize={18} barGap={4}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `R$${v}`}
                    width={64}
                  />
                  <Tooltip
                    formatter={(value: number | undefined) => formatCurrency(value ?? 0)}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Legend iconType="square" iconSize={10} />
                  <Bar dataKey="FIIs" stackId="a" fill="var(--color-accent-fii, #7c3aed)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Ações/BDRs" stackId="a" fill="var(--color-brand, #2563eb)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Asset breakdown table */}
        <section className="proventos__table-section">
          <h2 className="proventos__section-title">Detalhes por ativo</h2>
          {isLoading ? (
            <div className="proventos__table-skeleton" />
          ) : isEmpty ? (
            <div className="proventos__empty">
              <p className="proventos__empty-title">Nenhum provento encontrado</p>
              <p className="proventos__empty-text">
                Adicione ativos à sua carteira com quantidade maior que zero para ver as estimativas.
              </p>
            </div>
          ) : (
            <div className="proventos__table-wrap">
              <table className="proventos__table">
                <thead>
                  <tr>
                    <th>Ativo</th>
                    <th>Tipo</th>
                    <th className="proventos__col-num">Qtd.</th>
                    <th className="proventos__col-num">Valor/cota</th>
                    <th className="proventos__col-num">Data-com</th>
                    <th className="proventos__col-num">Estimativa</th>
                  </tr>
                </thead>
                <tbody>
                  {assetsWithDividends.map((asset) => (
                    <tr key={asset.ticker}>
                      <td className="proventos__col-ticker">{asset.ticker}</td>
                      <td><AssetTypeBadge type={asset.type} /></td>
                      <td className="proventos__col-num">{asset.quantity}</td>
                      <td className="proventos__col-num">{formatCurrency(asset.lastDividendValue)}</td>
                      <td className="proventos__col-num">{formatDateBr(asset.lastDividendDate)}</td>
                      <td className="proventos__col-num proventos__col-amount">
                        {formatCurrency(asset.projectedAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
